/** The person record's card layout (plan "Page anatomy" → Cards): the registry and one
 *  user → the six cards in page order (Contacts · Reachability · Identity · Access ·
 *  Origin · Technical), each split into its Editable block, the muted "From the app / OTA"
 *  vendor block and the System block. Pure — the cards' FieldSlots read the draft
 *  themselves. A catalog key the registry does not place is not shown (the contract test
 *  fails CI first); a registry key the catalog lacks is skipped.
 *
 *  Django admin access is not offered for guests (O1, the `not_guest` guard): a guest's
 *  Access card leaves it out — unless the guest has it anyway, which stays in view
 *  (read-only, the guard as the reason) so the anomaly is not hidden. */
import {
  USER_RECORD_SECTIONS,
  type UserRecordCatalog,
  type UserRecordEditContext,
  type UserRecordSection,
} from '@/api/userRecord';
import type { ConsoleUserDetail } from '@/api/users';
import { recordKey } from '@/features/records/draft/changeSet';
import type { FieldBlockKind, RecordViewDef, ViewBlock, ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { USER_SECTION_TITLES, type UserRegistry } from '../registry';

/** What a user view lays out. */
export interface UserRecordData {
  catalog: UserRecordCatalog;
  editContext: UserRecordEditContext;
  detail: ConsoleUserDetail;
}

/** The DOM id of a card — what the number keys scroll to. */
export function userSectionAnchorId(section: UserRecordSection): string {
  return `user-section-${section}`;
}

export const VENDOR_LABEL = 'From the app / OTA';

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'vendor', 'system'];

/** Whether a key is laid out for this user (Django admin access is not a guest's). */
function shownFor(key: string, { editContext: ctx }: Pick<UserRecordData, 'editContext'>): boolean {
  if (key !== 'is_staff' || ctx.kind !== 'guest') return true;
  return ctx.baselines[recordKey('user', ctx.user_id)]?.is_staff === true;
}

export function userSections(registry: UserRegistry, data: UserRecordData): ViewSection<UserRecordSection>[] {
  const { catalog, editContext: ctx } = data;
  const placed = catalog.fields
    .filter((f) => registry[f.key] !== undefined && shownFor(f.key, data))
    .sort((a, b) => registry[a.key]!.order - registry[b.key]!.order);

  return USER_RECORD_SECTIONS.map((section) => {
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields: ViewFieldRef[] = placed
        .filter((f) => registry[f.key]!.stage === section && registry[f.key]!.block === kind)
        .map((f) => ({ key: f.key, entity: 'user', id: ctx.user_id, path: f.key }));
      if (fields.length === 0) continue;
      blocks.push({
        kind,
        ...(kind === 'vendor' ? { label: VENDOR_LABEL } : kind === 'system' ? { label: 'System' } : {}),
        fields,
      });
    }
    return { id: section, title: USER_SECTION_TITLES[section], blocks };
  });
}

export const USER_CARDS_VIEW: RecordViewDef<UserRegistry, UserRecordData, UserRecordSection> = {
  id: 'cards',
  label: 'Cards',
  layout: userSections,
};
